/**
 * RTWO Blockchain Core Foundation — Block Runtime Service
 * RTWO v2.2 | Module ID: RTWO-BLCK-001
 */

import {
  BLOCKCHAIN_CORE_VERSION,
  type BlockLookupResult,
  type BlockchainCoreBlock,
  type BlockchainCoreDataStatus,
  type BlockchainCoreLookupType,
  type LatestBlockResult,
} from '../domain-models';
import { isBlockchainCoreEnabled } from '../configuration/blockchain-core-config';
import { assessBlockchainCoreConsensusReadiness } from './blockchain-core-consensus-readiness-service';
import type { IBlockchainCoreBlockRepository } from '../repositories/block-repository-interface';

const UNAVAILABLE_LABEL = 'Unavailable';
const NOT_CONFIGURED_LABEL = 'Not configured';

function resolveLabel(status: BlockchainCoreDataStatus): string {
  if (status === 'available') {
    return 'Available';
  }
  if (status === 'not_configured') {
    return NOT_CONFIGURED_LABEL;
  }
  return UNAVAILABLE_LABEL;
}

function buildSource(note: string): string {
  return `${BLOCKCHAIN_CORE_VERSION.moduleName} — ${note}`;
}

export class BlockRuntimeService {
  constructor(private readonly repository: IBlockchainCoreBlockRepository) {}

  getBlockCount(): number {
    if (!isBlockchainCoreEnabled() || !this.repository.isBackendConnected()) {
      return 0;
    }
    return this.repository.count();
  }

  getLatestBlock(): LatestBlockResult {
    const collectedAt = Date.now();
    const coreEnabled = isBlockchainCoreEnabled();
    const backendConnected = this.repository.isBackendConnected();

    if (!coreEnabled) {
      return {
        moduleId: 'RTWO-BLCK-001',
        phase: BLOCKCHAIN_CORE_VERSION.phase,
        available: false,
        status: 'not_configured',
        label: NOT_CONFIGURED_LABEL,
        block: null,
        blockCount: 0,
        backendConnected: false,
        consensusEnabled: false,
        collectedAt,
        source: buildSource('Blockchain Core disabled'),
      };
    }

    if (!backendConnected) {
      return {
        moduleId: 'RTWO-BLCK-001',
        phase: BLOCKCHAIN_CORE_VERSION.phase,
        available: false,
        status: 'unavailable',
        label: UNAVAILABLE_LABEL,
        block: null,
        blockCount: 0,
        backendConnected: false,
        consensusEnabled: false,
        collectedAt,
        source: buildSource('Backend not connected — Unavailable until real backend exists'),
      };
    }

    const block = this.repository.findLatest();
    const blockCount = this.repository.count();
    const consensusReadiness = assessBlockchainCoreConsensusReadiness({
      coreEnabled: true,
      backendConnected: true,
      blockCount,
    });

    if (!block) {
      return {
        moduleId: 'RTWO-BLCK-001',
        phase: BLOCKCHAIN_CORE_VERSION.phase,
        available: false,
        status: 'unavailable',
        label: UNAVAILABLE_LABEL,
        block: null,
        blockCount,
        backendConnected: true,
        consensusEnabled: false,
        collectedAt,
        source: buildSource('Backend connected — no indexed blocks available'),
      };
    }

    return {
      moduleId: 'RTWO-BLCK-001',
      phase: BLOCKCHAIN_CORE_VERSION.phase,
      available: true,
      status: 'available',
      label: 'Available',
      block,
      blockCount,
      backendConnected: true,
      consensusEnabled: consensusReadiness.consensusEnabled,
      collectedAt,
      source: buildSource(
        consensusReadiness.consensusEnabled
          ? 'Latest block from connected backend — consensus ready'
          : 'Latest block from connected backend — consensus readiness pending'
      ),
    };
  }

  lookupBlock(query: { height?: string | null; hash?: string | null }): BlockLookupResult {
    const collectedAt = Date.now();
    const coreEnabled = isBlockchainCoreEnabled();
    const backendConnected = this.repository.isBackendConnected();
    const height = query.height?.trim() ?? null;
    const hash = query.hash?.trim() ?? null;

    const baseUnavailable = (
      status: BlockchainCoreDataStatus,
      note: string,
      lookupType: BlockchainCoreLookupType | null = null,
      lookupValue: string | null = null
    ): BlockLookupResult => ({
      moduleId: 'RTWO-BLCK-001',
      phase: BLOCKCHAIN_CORE_VERSION.phase,
      available: false,
      status,
      label: resolveLabel(status),
      lookupType,
      lookupValue,
      block: null,
      backendConnected,
      consensusEnabled: false,
      collectedAt,
      source: buildSource(note),
    });

    if (!coreEnabled) {
      return baseUnavailable('not_configured', 'Blockchain Core disabled');
    }

    if (!height && !hash) {
      return baseUnavailable('unavailable', 'Block lookup requires height or hash query parameter');
    }

    if (!backendConnected) {
      return baseUnavailable(
        'unavailable',
        'Backend not connected — Unavailable until real backend exists',
        height ? 'height' : 'hash',
        height ?? hash
      );
    }

    let block: BlockchainCoreBlock | null = null;
    let lookupType: BlockchainCoreLookupType;
    let lookupValue: string;

    if (height) {
      lookupType = 'height';
      lookupValue = height;
      try {
        block = this.repository.findByNumber(BigInt(height));
      } catch {
        return baseUnavailable('unavailable', 'Invalid block height', lookupType, lookupValue);
      }
    } else {
      lookupType = 'hash';
      lookupValue = hash!;
      block = this.repository.findByHash(lookupValue);
    }

    if (!block) {
      return baseUnavailable(
        'unavailable',
        'Block not found in connected backend',
        lookupType,
        lookupValue
      );
    }

    return {
      moduleId: 'RTWO-BLCK-001',
      phase: BLOCKCHAIN_CORE_VERSION.phase,
      available: true,
      status: 'available',
      label: 'Available',
      lookupType,
      lookupValue,
      block,
      backendConnected: true,
      consensusEnabled: assessBlockchainCoreConsensusReadiness({
        coreEnabled: true,
        backendConnected: true,
        blockCount: this.repository.count(),
      }).consensusEnabled,
      collectedAt,
      source: buildSource('Block lookup from connected backend'),
    };
  }
}
